// Feature: features/usn-biller-icd10-compliance-tab.md
import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Flex,
  Input,
  Button,
  HStack,
  VStack,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Select,
  Alert,
  AlertIcon,
  Link,
  Divider,
} from '@chakra-ui/react';
import { ExternalLinkIcon, RepeatIcon } from '@chakra-ui/icons';
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

const GROUP_BY_OPTIONS = [
  { label: 'Payer Cohort (Commercial vs. MA vs. Medicare)', value: 'unified_payer_cohort' },
  { label: 'Procedure Specialty (Spine / Pain / ENT / Cranial / Vascular)', value: 'canonical_procedure_group' },
  { label: 'Biller Team (MC Billing / Collective Current / ANS)', value: 'biller_team' },
  { label: 'Biller Employee (Individual Biller)', value: 'biller_employee' },
  { label: 'Revedy Approval Score Band', value: 'approval_score_band' },
];

const OUTCOME_FILTER_OPTIONS = [
  { label: 'Medical Necessity or Medical Record Denials/Holds', value: 'med_nec_or_records' },
  { label: 'Medical Necessity / Diagnosis Denials Only', value: 'med_nec_only' },
  { label: 'Medical Record Holds Only (CARC 252 / M127 / N350)', value: 'med_records_only' },
  { label: 'Coding / Bundling / MUE Denials Only', value: 'coding_only' },
  { label: 'Any First-Pass Denial or Hold (Incl. Partial Pay Lines)', value: 'any_denial_or_hold' },
  { label: 'Clean Approved (All Lines Paid or Applied to Deductible)', value: 'clean_approved' },
  { label: 'All Revedy-Coded 835 Claims', value: 'all_coded' },
];

const isPureDeductibleApproved = (r) =>
  r.fp_is_clean_paid !== 1 &&
  r.fp_is_partial_paid !== 1 &&
  String(r.fp_stage || '').startsWith('7. Deductible');

const isCleanApproved = (r) => r.fp_is_clean_paid === 1 || isPureDeductibleApproved(r);

const hasMedNecOrRecordIssue = (r) =>
  r.fp_has_clinical_denial === 1 || r.fp_has_stage2_hold === 1;

const hasAnyDenialOrHold = (r) =>
  r.fp_has_clinical_denial === 1 ||
  r.fp_has_stage2_hold === 1 ||
  r.fp_has_coding_denial === 1;

const getFirstPassOutcomeBadges = (r) => {
  const badges = [];
  if (r.fp_is_clean_paid === 1) {
    badges.push({ label: 'Clean Pay (All Lines Paid)', color: 'green' });
  } else if (isPureDeductibleApproved(r)) {
    badges.push({ label: 'Approved (Applied to Deductible)', color: 'teal' });
  } else if (r.fp_is_partial_paid === 1) {
    badges.push({ label: 'Partial Pay (Has $0 Denied Lines)', color: 'yellow' });
  } else {
    badges.push({ label: 'Denied / Held ($0 Paid)', color: 'red' });
  }

  if (r.fp_has_clinical_denial === 1) {
    badges.push({ label: 'Medical Necessity / Dx Denial', color: 'red' });
  }
  if (r.fp_has_stage2_hold === 1) {
    badges.push({ label: 'Medical Record Hold', color: 'purple' });
  }
  if (r.fp_has_coding_denial === 1) {
    badges.push({ label: 'Coding / Bundling / MUE Denial', color: 'orange' });
  }
  return badges;
};

const cleanReasonText = (raw) => {
  if (!raw) return '';
  return String(raw).replace(/^Stage\s+[0-9A-Z]+:\s*/i, '').trim();
};

const calcRate = (arr, predicateOrField) => {
  if (!arr || arr.length === 0) return 0;
  const sum = arr.reduce((acc, r) => {
    const hit =
      typeof predicateOrField === 'function' ? predicateOrField(r) : r[predicateOrField] === 1;
    return acc + (hit ? 1 : 0);
  }, 0);
  return Number(((sum / arr.length) * 100).toFixed(1));
};

const USNClaimsFirstPassDashboard = () => {
  const { accessToken } = useContext(AppContext);

  const [claims, setClaims] = useState([]);
  const [fileMeta, setFileMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [dosWindow, setDosWindow] = useState('all_ytd');
  const [specialtyScope, setSpecialtyScope] = useState('ALL');
  const [matchMode, setMatchMode] = useState('strict'); // 'strict' | 'compliant'
  const [groupCol, setGroupCol] = useState('unified_payer_cohort');
  const [selectedGroupVal, setSelectedGroupVal] = useState('ALL');
  const [timeAxis, setTimeAxis] = useState('first_remit');
  const [timeGrain, setTimeGrain] = useState('month');
  const [outcomeFilterMode, setOutcomeFilterMode] = useState('med_nec_or_records');
  const [recentLimit, setRecentLimit] = useState(200);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchFirstPassClaims = async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await callApi('/usn/claims-first-pass', accessToken, { method: 'GET' });
      if (res && res.ok) {
        const result = await res.json();
        if (result.status === 'success') {
          setClaims(result.claims || []);
          setFileMeta({
            upload_id: result.upload_id,
            user_id: result.user_id,
            org_id: result.org_id,
            file_type: result.file_type,
            filename: result.filename,
            row_count: result.row_count,
            record_count: result.record_count,
            created_at: result.created_at,
            rebuilt_at: result.rebuilt_at,
            metadata: result.metadata || {},
          });
        } else {
          setError(result.message || 'Failed to load First-Pass 835 claims.');
        }
      } else {
        setError(`Server returned ${res ? res.status : 'no response'}`);
      }
    } catch (err) {
      console.error('Error fetching First-Pass 835 claims:', err);
      setError('Failed to fetch First-Pass 835 claims data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFirstPassClaims();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const matchField = matchMode === 'strict' ? 'is_strict_revedy_both' : 'is_compliant_revedy_both';

  const bucketCol = useMemo(() => {
    if (timeAxis === 'dos') {
      return timeGrain === 'week' ? 'dos_week' : 'dos_month';
    }
    return timeGrain === 'week' ? 'first_remit_week' : 'first_remit_month';
  }, [timeAxis, timeGrain]);

  const baseFilteredClaims = useMemo(() => {
    return claims.filter((c) => {
      if (dosWindow === 'aug_plus' && c.is_prod_aug_plus !== 1) {
        return false;
      }
      if (specialtyScope === 'SURGICAL_IONM' && c.is_pain_procedure === 1) {
        return false;
      }
      if (
        specialtyScope !== 'ALL' &&
        specialtyScope !== 'SURGICAL_IONM' &&
        c.canonical_procedure_group !== specialtyScope
      ) {
        return false;
      }
      if (selectedGroupVal !== 'ALL' && (c[groupCol] || 'Unassigned') !== selectedGroupVal) {
        return false;
      }
      return true;
    });
  }, [claims, dosWindow, specialtyScope, selectedGroupVal, groupCol]);

  const availableGroupValues = useMemo(() => {
    const counts = {};
    baseFilteredClaims
      .filter((c) => c.has_revedy_both === 1)
      .forEach((c) => {
        const g = c[groupCol] || 'Unassigned';
        counts[g] = (counts[g] || 0) + 1;
      });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([val, cnt]) => ({ value: val, count: cnt }));
  }, [baseFilteredClaims, groupCol]);

  // Cohort Split: (1) Coded as Revedy, (2) Biller Modified, (3) All Revedy-Coded
  const cohorts = useMemo(() => {
    const revCoded = baseFilteredClaims.filter((c) => c.has_revedy_both === 1);
    const codedAsRevedy = revCoded.filter((c) => c[matchField] === 1);
    const billerModified = revCoded.filter((c) => c[matchField] === 0);
    return {
      all835: baseFilteredClaims,
      revCoded,
      codedAsRevedy,
      billerModified,
    };
  }, [baseFilteredClaims, matchField]);

  const kpis = useMemo(() => {
    const rev = cohorts.codedAsRevedy;
    const mod = cohorts.billerModified;
    const all = cohorts.revCoded;

    const revCleanApp = calcRate(rev, isCleanApproved);
    const modCleanApp = calcRate(mod, isCleanApproved);

    const revCleanCash = calcRate(rev, 'fp_is_clean_paid');
    const modCleanCash = calcRate(mod, 'fp_is_clean_paid');

    const revMedNec = calcRate(rev, 'fp_has_clinical_denial');
    const modMedNec = calcRate(mod, 'fp_has_clinical_denial');

    const revMedRec = calcRate(rev, 'fp_has_stage2_hold');
    const modMedRec = calcRate(mod, 'fp_has_stage2_hold');

    const revMedNecOrRec = calcRate(rev, hasMedNecOrRecordIssue);
    const modMedNecOrRec = calcRate(mod, hasMedNecOrRecordIssue);

    const revCoding = calcRate(rev, 'fp_has_coding_denial');
    const modCoding = calcRate(mod, 'fp_has_coding_denial');

    return {
      revCount: rev.length,
      modCount: mod.length,
      allCodedCount: all.length,
      revCleanApp,
      modCleanApp,
      cleanAppLift: Number((revCleanApp - modCleanApp).toFixed(1)),
      revCleanCash,
      modCleanCash,
      revMedNec,
      modMedNec,
      revMedRec,
      modMedRec,
      revMedNecOrRec,
      modMedNecOrRec,
      revCoding,
      modCoding,
      codingDiff: Number((revCoding - modCoding).toFixed(1)),
    };
  }, [cohorts]);

  // Chart 1: First-Pass EOB Breakout by Cohort
  const headToHeadData = useMemo(() => {
    const rev = cohorts.codedAsRevedy;
    const mod = cohorts.billerModified;
    const all = cohorts.revCoded;
    return [
      {
        metric: 'Clean Approved (Paid+Ded) %',
        CodedAsRevedy: calcRate(rev, isCleanApproved),
        BillerModified: calcRate(mod, isCleanApproved),
        OverallCoded: calcRate(all, isCleanApproved),
      },
      {
        metric: 'Partial Pay %',
        CodedAsRevedy: calcRate(rev, 'fp_is_partial_paid'),
        BillerModified: calcRate(mod, 'fp_is_partial_paid'),
        OverallCoded: calcRate(all, 'fp_is_partial_paid'),
      },
      {
        metric: 'Med Necessity / Dx Denial %',
        CodedAsRevedy: calcRate(rev, 'fp_has_clinical_denial'),
        BillerModified: calcRate(mod, 'fp_has_clinical_denial'),
        OverallCoded: calcRate(all, 'fp_has_clinical_denial'),
      },
      {
        metric: 'Medical Record Hold %',
        CodedAsRevedy: calcRate(rev, 'fp_has_stage2_hold'),
        BillerModified: calcRate(mod, 'fp_has_stage2_hold'),
        OverallCoded: calcRate(all, 'fp_has_stage2_hold'),
      },
      {
        metric: 'Coding / Bundling / MUE %',
        CodedAsRevedy: calcRate(rev, 'fp_has_coding_denial'),
        BillerModified: calcRate(mod, 'fp_has_coding_denial'),
        OverallCoded: calcRate(all, 'fp_has_coding_denial'),
      },
    ];
  }, [cohorts]);

  // Chart 2: First-Pass Clean Approved %, Med Necessity/Records %, & Coding Denial % Over Time
  const trendData = useMemo(() => {
    const map = {};
    cohorts.revCoded.forEach((c) => {
      const bkt = c[bucketCol];
      if (!bkt) return;
      if (!map[bkt]) {
        map[bkt] = {
          bucket: bkt,
          revN: 0,
          revCleanApp: 0,
          revMnRec: 0,
          revCoding: 0,
          modN: 0,
          modCleanApp: 0,
          modMnRec: 0,
          modCoding: 0,
        };
      }
      if (c[matchField] === 1) {
        map[bkt].revN += 1;
        if (isCleanApproved(c)) map[bkt].revCleanApp += 1;
        if (hasMedNecOrRecordIssue(c)) map[bkt].revMnRec += 1;
        if (c.fp_has_coding_denial === 1) map[bkt].revCoding += 1;
      } else {
        map[bkt].modN += 1;
        if (isCleanApproved(c)) map[bkt].modCleanApp += 1;
        if (hasMedNecOrRecordIssue(c)) map[bkt].modMnRec += 1;
        if (c.fp_has_coding_denial === 1) map[bkt].modCoding += 1;
      }
    });

    return Object.values(map)
      .sort((a, b) => a.bucket.localeCompare(b.bucket))
      .map((d) => ({
        bucket: d.bucket,
        CodedClaims: d.revN + d.modN,
        RevedyCleanApprovedPct:
          d.revN > 0 ? Number(((d.revCleanApp / d.revN) * 100).toFixed(1)) : null,
        ModifiedCleanApprovedPct:
          d.modN > 0 ? Number(((d.modCleanApp / d.modN) * 100).toFixed(1)) : null,
        RevedyMedNecOrRecPct:
          d.revN > 0 ? Number(((d.revMnRec / d.revN) * 100).toFixed(1)) : null,
        ModifiedMedNecOrRecPct:
          d.modN > 0 ? Number(((d.modMnRec / d.modN) * 100).toFixed(1)) : null,
        ModifiedCodingDenialPct:
          d.modN > 0 ? Number(((d.modCoding / d.modN) * 100).toFixed(1)) : null,
      }));
  }, [cohorts.revCoded, bucketCol, matchField]);

  // Group Scorecard Table
  const groupScorecardRows = useMemo(() => {
    const map = {};
    cohorts.revCoded.forEach((c) => {
      const g = c[groupCol] || 'Unassigned';
      if (!map[g]) {
        map[g] = { group: g, rev: [], mod: [], all: [] };
      }
      map[g].all.push(c);
      if (c[matchField] === 1) {
        map[g].rev.push(c);
      } else {
        map[g].mod.push(c);
      }
    });

    return Object.values(map)
      .sort((a, b) => b.all.length - a.all.length)
      .slice(0, 20)
      .map((item) => {
        const revCleanApp = calcRate(item.rev, isCleanApproved);
        const modCleanApp = calcRate(item.mod, isCleanApproved);
        return {
          group: item.group,
          totalCoded: item.all.length,
          revCount: item.rev.length,
          modCount: item.mod.length,
          revCleanAppPct: revCleanApp,
          modCleanAppPct: modCleanApp,
          cleanLift:
            item.rev.length > 0 && item.mod.length > 0
              ? Number((revCleanApp - modCleanApp).toFixed(1))
              : null,
          revMedNecPct: calcRate(item.rev, 'fp_has_clinical_denial'),
          modMedNecPct: calcRate(item.mod, 'fp_has_clinical_denial'),
          revMedRecPct: calcRate(item.rev, 'fp_has_stage2_hold'),
          modMedRecPct: calcRate(item.mod, 'fp_has_stage2_hold'),
          revCodingPct: calcRate(item.rev, 'fp_has_coding_denial'),
          modCodingPct: calcRate(item.mod, 'fp_has_coding_denial'),
        };
      });
  }, [cohorts.revCoded, groupCol, matchField]);

  // Bottom Table: Most Recent N First-Pass Claims
  const { recentRows, totalMatching } = useMemo(() => {
    let rows = [...cohorts.revCoded];
    if (outcomeFilterMode === 'med_nec_or_records') {
      rows = rows.filter(hasMedNecOrRecordIssue);
    } else if (outcomeFilterMode === 'med_nec_only') {
      rows = rows.filter((c) => c.fp_has_clinical_denial === 1);
    } else if (outcomeFilterMode === 'med_records_only') {
      rows = rows.filter((c) => c.fp_has_stage2_hold === 1);
    } else if (outcomeFilterMode === 'coding_only') {
      rows = rows.filter((c) => c.fp_has_coding_denial === 1);
    } else if (outcomeFilterMode === 'any_denial_or_hold') {
      rows = rows.filter(hasAnyDenialOrHold);
    } else if (outcomeFilterMode === 'clean_approved') {
      rows = rows.filter(isCleanApproved);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      rows = rows.filter(
        (c) =>
          (c.pid || '').toLowerCase().includes(q) ||
          (c.payer_name || '').toLowerCase().includes(q) ||
          (c.unified_payer_cohort || '').toLowerCase().includes(q) ||
          (c.biller_team || '').toLowerCase().includes(q) ||
          (c.biller_employee || '').toLowerCase().includes(q) ||
          (c.procedure_name || '').toLowerCase().includes(q) ||
          (c.fp_carcs || '').toLowerCase().includes(q) ||
          (c.fp_rarcs || '').toLowerCase().includes(q) ||
          (c.revedy_icds_dotted || '').toLowerCase().includes(q) ||
          (c.submitted_icds_dotted || '').toLowerCase().includes(q) ||
          (c.claim_base_cpts || '').toLowerCase().includes(q)
      );
    }

    rows.sort((a, b) => {
      const rCmp = (b.first_remit_date || '').localeCompare(a.first_remit_date || '');
      if (rCmp !== 0) return rCmp;
      return (b.dos || '').localeCompare(a.dos || '');
    });

    return {
      totalMatching: rows.length,
      recentRows: rows.slice(0, recentLimit),
    };
  }, [cohorts.revCoded, outcomeFilterMode, searchQuery, recentLimit]);

  const exportToCSV = () => {
    if (!recentRows.length) return;
    const headers = [
      'PID',
      'ClaimID',
      'DOS',
      'First_Remit_Date',
      'Payer_Cohort',
      'Payer_Name',
      'Specialty',
      'Procedure',
      'Biller_Team',
      'Biller_Employee',
      'Revedy_Combined_Tier',
      'Approval_Score',
      'Clean_Approved',
      'Med_Necessity_Denial',
      'Med_Record_Hold',
      'Coding_Bundling_MUE_Denial',
      'First_Pass_CARCs',
      'First_Pass_RARCs',
      'First_Pass_Reason',
      'Revedy_Coded_ICDs',
      'Submitted_ICDs',
      'Revedy_Base_CPTs',
      'Submitted_Base_CPTs',
      'CPTs_Added_By_USN',
    ];
    const escapeCSV = (val) => {
      const s = val == null ? '' : String(val);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const csvRows = recentRows.map((r) =>
      [
        r.pid,
        r.claim_id,
        r.dos,
        r.first_remit_date,
        r.unified_payer_cohort,
        r.payer_name,
        r.canonical_procedure_group,
        r.procedure_name,
        r.biller_team,
        r.biller_employee,
        r.revedy_combined_tier,
        r.approval_score,
        isCleanApproved(r) ? 'Yes' : 'No',
        r.fp_has_clinical_denial === 1 ? 'Yes' : 'No',
        r.fp_has_stage2_hold === 1 ? 'Yes' : 'No',
        r.fp_has_coding_denial === 1 ? 'Yes' : 'No',
        r.fp_carcs,
        r.fp_rarcs,
        cleanReasonText(r.fp_primary_subcategory),
        r.revedy_icds_dotted,
        r.submitted_icds_dotted,
        r.revedy_base_cpts,
        r.claim_base_cpts,
        r.cpts_added_by_usn,
      ]
        .map(escapeCSV)
        .join(',')
    );
    const csvContent = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `usn_first_pass_claims_${recentRows.length}_${new Date().toISOString().split('T')[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Box p={6} maxW="1500px" mx="auto">
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4} mb={4}>
          <Box>
            <Heading size="lg" color="gray.800">
              USN First-Pass 835 EOB: Clean Approvals, Medical Necessity / Record Holds &amp; Coding Denials
            </Heading>
            <Text fontSize="sm" color="gray.600">
              Initial 835 EOB (`remit_date == first_remit_date`) outcomes comparing claims{' '}
              <strong>Coded as Revedy (ICD-10 + CPT)</strong> vs. <strong>Biller Modified / Overridden</strong> (including claims where approved lines went to patient deductible).
            </Text>
          </Box>
          <HStack spacing={2}>
            <Badge colorScheme="purple">file_type: {fileMeta?.file_type || 'usn_waystar_835_first_pass'}</Badge>
            {fileMeta?.org_id != null && <Badge colorScheme="blue">org_id: {fileMeta.org_id}</Badge>}
            <Button
              size="sm"
              variant="outline"
              leftIcon={<RepeatIcon />}
              onClick={fetchFirstPassClaims}
              isLoading={loading}
            >
              Refresh
            </Button>
          </HStack>
        </Flex>

        {error && (
          <Alert status="error" borderRadius="md" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {loading ? (
          <Flex justify="center" align="center" py={16}>
            <Spinner size="xl" color="blue.500" mr={3} />
            <Text color="gray.600">Loading First-Pass 835 EOB dataset...</Text>
          </Flex>
        ) : (
          <>
            {/* Filter Controls */}
            <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
              <SimpleGrid columns={{ base: 1, md: 6 }} spacing={3}>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                    1. DOS Cohort Window
                  </Text>
                  <Select size="sm" value={dosWindow} onChange={(e) => setDosWindow(e.target.value)}>
                    <option value="all_ytd">Full 2026 YTD (All 835 Claims)</option>
                    <option value="aug_plus">Production Window (DOS &ge; 2026-08-01)</option>
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                    2. Procedure Specialty
                  </Text>
                  <Select size="sm" value={specialtyScope} onChange={(e) => setSpecialtyScope(e.target.value)}>
                    <option value="ALL">All Specialties</option>
                    <option value="SURGICAL_IONM">Surgical IONM Only (Excludes Pain)</option>
                    <option value="1. Spine Surgery">1. Spine Surgery</option>
                    <option value="2. Pain Procedure (TFESI / MBB / RFA / ESI / SCS)">2. Pain Procedures</option>
                    <option value="3. ENT / Thyroid / Parotid Surgery">3. ENT / Thyroid / Parotid</option>
                    <option value="4. Brain / Cranial / Endovascular Surgery">4. Brain / Cranial</option>
                    <option value="5. Vascular / Carotid Surgery">5. Vascular / Carotid</option>
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                    3. Compliance Match Mode
                  </Text>
                  <Select size="sm" value={matchMode} onChange={(e) => setMatchMode(e.target.value)}>
                    <option value="strict">Strict Match (Exact ICD Seq + Base CPT)</option>
                    <option value="compliant">Revedy-Compliant (Exact/Subset ICD + Base CPT)</option>
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                    4. Group By Dimension
                  </Text>
                  <Select
                    size="sm"
                    value={groupCol}
                    onChange={(e) => {
                      setGroupCol(e.target.value);
                      setSelectedGroupVal('ALL');
                    }}
                  >
                    {GROUP_BY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                    5. Filter Specific Group
                  </Text>
                  <Select size="sm" value={selectedGroupVal} onChange={(e) => setSelectedGroupVal(e.target.value)}>
                    <option value="ALL">All Groups</option>
                    {availableGroupValues.map((g) => (
                      <option key={g.value} value={g.value}>
                        {g.value} (n={g.count})
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                    6. Trend Time Axis &amp; Grain
                  </Text>
                  <HStack spacing={1}>
                    <Select size="sm" value={timeAxis} onChange={(e) => setTimeAxis(e.target.value)}>
                      <option value="first_remit">1st Remit</option>
                      <option value="dos">DOS</option>
                    </Select>
                    <Select size="sm" value={timeGrain} onChange={(e) => setTimeGrain(e.target.value)}>
                      <option value="month">Monthly</option>
                      <option value="week">Weekly</option>
                    </Select>
                  </HStack>
                </Box>
              </SimpleGrid>
            </Box>

            {/* Top 5 First-Pass EOB KPI Cards */}
            <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4} mb={6}>
              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px" borderLeft="4px solid #059669">
                <Stat>
                  <StatLabel color="gray.600" fontSize="xs">
                    1st-Pass Clean Approved % (Paid + Ded)
                  </StatLabel>
                  <StatNumber fontSize="2xl" color="green.600">
                    {kpis.revCleanApp}%{' '}
                    <Text as="span" fontSize="sm" color="gray.500">
                      vs. {kpis.modCleanApp}% Mod
                    </Text>
                  </StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>
                    <Badge colorScheme={kpis.cleanAppLift >= 0 ? 'green' : 'red'}>
                      {kpis.cleanAppLift >= 0 ? `+${kpis.cleanAppLift}%` : `${kpis.cleanAppLift}%`} Lift
                    </Badge>{' '}
                    (Cash Clean: {kpis.revCleanCash}% vs. {kpis.modCleanCash}%)
                  </StatHelpText>
                </Stat>
              </Box>

              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px" borderLeft="4px solid #DC2626">
                <Stat>
                  <StatLabel color="gray.600" fontSize="xs">
                    Med Necessity / Dx Denial %
                  </StatLabel>
                  <StatNumber fontSize="2xl" color="red.600">
                    {kpis.revMedNec}%{' '}
                    <Text as="span" fontSize="sm" color="gray.500">
                      vs. {kpis.modMedNec}% Mod
                    </Text>
                  </StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>
                    CARC 50, 55, 56, 11, 167, 150 / N623, M76, N115
                  </StatHelpText>
                </Stat>
              </Box>

              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px" borderLeft="4px solid #7C3AED">
                <Stat>
                  <StatLabel color="gray.600" fontSize="xs">
                    Medical Record Hold %
                  </StatLabel>
                  <StatNumber fontSize="2xl" color="purple.600">
                    {kpis.revMedRec}%{' '}
                    <Text as="span" fontSize="sm" color="gray.500">
                      vs. {kpis.modMedRec}% Mod
                    </Text>
                  </StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>
                    CARC 252, 251, 226 / M127, N350, N705
                  </StatHelpText>
                </Stat>
              </Box>

              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px" borderLeft="4px solid #2563EB">
                <Stat>
                  <StatLabel color="gray.600" fontSize="xs">
                    Combined Med Nec + Record Hold %
                  </StatLabel>
                  <StatNumber fontSize="2xl" color="blue.600">
                    {kpis.revMedNecOrRec}%{' '}
                    <Text as="span" fontSize="sm" color="gray.500">
                      vs. {kpis.modMedNecOrRec}% Mod
                    </Text>
                  </StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>
                    n={kpis.revCount.toLocaleString()} Revedy vs. {kpis.modCount.toLocaleString()} Mod
                  </StatHelpText>
                </Stat>
              </Box>

              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px" borderLeft="4px solid #F59E0B">
                <Stat>
                  <StatLabel color="gray.600" fontSize="xs">
                    Coding / Bundling / MUE Denial %
                  </StatLabel>
                  <StatNumber fontSize="2xl" color="orange.600">
                    {kpis.revCoding}%{' '}
                    <Text as="span" fontSize="sm" color="gray.500">
                      vs. {kpis.modCoding}% Mod
                    </Text>
                  </StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>
                    <Badge colorScheme={kpis.codingDiff <= 0 ? 'green' : 'red'}>
                      {kpis.codingDiff <= 0 ? `${kpis.codingDiff}%` : `+${kpis.codingDiff}%`} Coding Denials
                    </Badge>{' '}
                    (CARC 97, 4, 151, 222, B15)
                  </StatHelpText>
                </Stat>
              </Box>
            </SimpleGrid>

            {/* Top Charts Row */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
              <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                <Heading size="sm" mb={1} color="gray.700">
                  1. First-Pass EOB Breakout by Cohort (Clean Approved, Med Necessity, Records &amp; Coding)
                </Heading>
                <Text fontSize="xs" color="gray.500" mb={3}>
                  Comparing (1) Coded as Revedy (Green), (2) Biller Modified / Overridden (Red), and (3) Overall Benchmark (Slate).
                </Text>
                <Box height="340px">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={headToHeadData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="metric" tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="CodedAsRevedy" name="1. Coded as Revedy (ICD+CPT)" fill="#059669" />
                      <Bar dataKey="BillerModified" name="2. Biller Modified / Overrode" fill="#DC2626" />
                      <Bar dataKey="OverallCoded" name="3. Overall Benchmark" fill="#475569" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                <Heading size="sm" mb={1} color="gray.700">
                  2. First-Pass Clean Approved % &amp; Denial Trends Over Time ({bucketCol})
                </Heading>
                <Text fontSize="xs" color="gray.500" mb={3}>
                  Lines show Clean Approved % (Revedy vs. Mod), Med Necessity/Records %, and Coding Denial %. Bars show Coded Volume.
                </Text>
                <Box height="340px">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 25 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
                      <YAxis yAxisId="left" domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar yAxisId="right" dataKey="CodedClaims" name="Coded Claims (n)" fill="#E2E8F0" barSize={22} />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="RevedyCleanApprovedPct"
                        name="Revedy Clean Approved %"
                        stroke="#059669"
                        strokeWidth={3}
                        dot={{ r: 3 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="ModifiedCleanApprovedPct"
                        name="Modified Clean Approved %"
                        stroke="#2563EB"
                        strokeWidth={2.5}
                        dot={{ r: 3 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="RevedyMedNecOrRecPct"
                        name="Revedy MedNec/Record %"
                        stroke="#7C3AED"
                        strokeWidth={2}
                        strokeDasharray="4 4"
                        dot={{ r: 2 }}
                      />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="ModifiedCodingDenialPct"
                        name="Modified Coding Denial %"
                        stroke="#DC2626"
                        strokeWidth={2.5}
                        strokeDasharray="5 5"
                        dot={{ r: 3 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </SimpleGrid>

            {/* Group Scorecard Table */}
            <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
              <Heading size="sm" mb={1} color="gray.700">
                3. First-Pass EOB Scorecard by {groupCol}
              </Heading>
              <Text fontSize="xs" color="gray.500" mb={3}>
                Side-by-side Clean Approved % (Paid + Deductible), Medical Necessity Denial %, Medical Record Hold %, and Coding Denial %.
              </Text>
              <Box overflowX="auto" maxH="360px" overflowY="auto">
                <Table variant="simple" size="sm">
                  <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
                    <Tr>
                      <Th>Group ({groupCol})</Th>
                      <Th isNumeric>Coded Claims</Th>
                      <Th isNumeric>Revedy (n / Mod n)</Th>
                      <Th isNumeric>Revedy Clean Approved %</Th>
                      <Th isNumeric>Mod Clean Approved %</Th>
                      <Th isNumeric>Clean Lift</Th>
                      <Th isNumeric>Rev / Mod Med Nec %</Th>
                      <Th isNumeric>Rev / Mod Med Record %</Th>
                      <Th isNumeric>Rev / Mod Coding Den %</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {groupScorecardRows.map((g) => (
                      <Tr key={g.group} _hover={{ bg: 'gray.50' }}>
                        <Td fontWeight="semibold" fontSize="xs">
                          {g.group}
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {g.totalCoded.toLocaleString()}
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {g.revCount} / {g.modCount}
                        </Td>
                        <Td isNumeric fontWeight="bold" color="green.600" fontSize="xs">
                          {g.revCount > 0 ? `${g.revCleanAppPct}%` : '-'}
                        </Td>
                        <Td isNumeric color="gray.700" fontSize="xs">
                          {g.modCount > 0 ? `${g.modCleanAppPct}%` : '-'}
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {g.cleanLift !== null ? (
                            <Badge colorScheme={g.cleanLift >= 0 ? 'green' : 'red'}>
                              {g.cleanLift >= 0 ? `+${g.cleanLift}%` : `${g.cleanLift}%`}
                            </Badge>
                          ) : (
                            '-'
                          )}
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {g.revCount > 0 ? `${g.revMedNecPct}%` : '-'} /{' '}
                          <Text as="span" color="red.600" fontWeight="semibold">
                            {g.modCount > 0 ? `${g.modMedNecPct}%` : '-'}
                          </Text>
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {g.revCount > 0 ? `${g.revMedRecPct}%` : '-'} /{' '}
                          <Text as="span" color="purple.600" fontWeight="semibold">
                            {g.modCount > 0 ? `${g.modMedRecPct}%` : '-'}
                          </Text>
                        </Td>
                        <Td isNumeric fontSize="xs">
                          {g.revCount > 0 ? `${g.revCodingPct}%` : '-'} /{' '}
                          <Text as="span" color="orange.600" fontWeight="semibold">
                            {g.modCount > 0 ? `${g.modCodingPct}%` : '-'}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>

            <Divider my={6} />

            {/* Bottom Table: Most Recent 200 First-Pass Claims */}
            <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
              <Flex justify="space-between" align="center" flexWrap="wrap" gap={4} mb={4}>
                <Box>
                  <Heading size="md" color="gray.800">
                    Most Recent {recentLimit} First-Pass Claims
                  </Heading>
                  <Text fontSize="xs" color="gray.600">
                    Showing <strong>{recentRows.length.toLocaleString()}</strong> of{' '}
                    <strong>{totalMatching.toLocaleString()}</strong> matching claims (sorted by most recent 1st EOB Remit Date descending).
                  </Text>
                </Box>
                <Button
                  size="sm"
                  colorScheme="green"
                  onClick={exportToCSV}
                  isDisabled={recentRows.length === 0}
                >
                  Export These {recentRows.length} Claims to CSV
                </Button>
              </Flex>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                    First-Pass Outcome Filter
                  </Text>
                  <Select
                    size="sm"
                    value={outcomeFilterMode}
                    onChange={(e) => setOutcomeFilterMode(e.target.value)}
                  >
                    {OUTCOME_FILTER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                    Max Recent Claims to Display
                  </Text>
                  <Select
                    size="sm"
                    value={recentLimit}
                    onChange={(e) => setRecentLimit(Number(e.target.value))}
                  >
                    <option value={50}>Most Recent 50 Claims</option>
                    <option value={100}>Most Recent 100 Claims</option>
                    <option value={200}>Most Recent 200 Claims</option>
                    <option value={500}>Most Recent 500 Claims</option>
                    <option value={1000}>Most Recent 1,000 Claims</option>
                  </Select>
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
                    Quick Search (PID, Payer, CARC, RARC, Biller, ICD, CPT)
                  </Text>
                  <Input
                    size="sm"
                    placeholder="e.g. CARC 55 or M127 or N623 or Aetna..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </Box>
              </SimpleGrid>

              <Box overflowX="auto" maxH="650px" overflowY="auto">
                <Table variant="simple" size="sm">
                  <Thead position="sticky" top={0} bg="gray.100" zIndex={1}>
                    <Tr>
                      <Th>PID.Claim</Th>
                      <Th>1st Remit / DOS</Th>
                      <Th>Payer / Specialty</Th>
                      <Th>Biller (Team)</Th>
                      <Th>Revedy Compliance Tier</Th>
                      <Th>1st-Pass Outcome &amp; CARC/RARC</Th>
                      <Th>Revedy vs. Submitted ICDs</Th>
                      <Th>Revedy vs. Submitted Base CPTs</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {recentRows.map((r) => {
                      const badges = getFirstPassOutcomeBadges(r);
                      return (
                        <Tr key={r.claim_key} _hover={{ bg: 'gray.50' }}>
                          <Td fontWeight="bold" whiteSpace="nowrap">
                            <Link href={`/ionm-strict-analysis/${r.pid}`} isExternal color="blue.600">
                              {r.pid}.{r.claim_id} <ExternalLinkIcon mx="2px" />
                            </Link>
                            {r.approval_score != null && (
                              <Box mt={1}>
                                <Badge
                                  colorScheme={
                                    r.approval_score >= 80
                                      ? 'green'
                                      : r.approval_score >= 50
                                      ? 'yellow'
                                      : 'red'
                                  }
                                  fontSize="10px"
                                >
                                  Score: {r.approval_score}
                                </Badge>
                              </Box>
                            )}
                          </Td>
                          <Td whiteSpace="nowrap" fontSize="xs">
                            <Text fontWeight="semibold">Remit: {r.first_remit_date || 'N/A'}</Text>
                            <Text color="gray.500">DOS: {r.dos || 'N/A'}</Text>
                          </Td>
                          <Td fontSize="xs">
                            <Text fontWeight="semibold" maxW="170px" isTruncated title={r.payer_name}>
                              {r.payer_name}
                            </Text>
                            <Text color="gray.500" fontSize="10px">
                              {r.canonical_procedure_group}
                            </Text>
                          </Td>
                          <Td fontSize="xs">
                            <Text fontWeight="medium">{r.biller_employee || 'Unassigned'}</Text>
                            <Badge colorScheme="purple" fontSize="10px">
                              {r.biller_team || 'Unassigned'}
                            </Badge>
                          </Td>
                          <Td fontSize="xs">
                            <Badge
                              colorScheme={r[matchField] === 1 ? 'green' : 'red'}
                              whiteSpace="normal"
                              fontSize="10px"
                            >
                              {r.revedy_combined_tier}
                            </Badge>
                          </Td>
                          <Td fontSize="xs">
                            <VStack align="start" spacing={1}>
                              {badges.map((b, idx) => (
                                <Badge key={idx} colorScheme={b.color} fontSize="10px">
                                  {b.label}
                                </Badge>
                              ))}
                            </VStack>
                            <Text fontSize="10px" color="gray.700" mt={1}>
                              <strong>CARC:</strong> {r.fp_carcs || '-'} | <strong>RARC:</strong>{' '}
                              {r.fp_rarcs || '-'}
                            </Text>
                            <Text
                              fontSize="10px"
                              color="gray.500"
                              maxW="220px"
                              isTruncated
                              title={cleanReasonText(r.fp_primary_subcategory)}
                            >
                              {cleanReasonText(r.fp_primary_subcategory)}
                            </Text>
                          </Td>
                          <Td fontSize="xs" fontFamily="mono">
                            <Text color="green.700" title={r.revedy_icds_dotted}>
                              <strong>Rev:</strong> {r.revedy_icds_dotted || '-'}
                            </Text>
                            <Text color="orange.700" title={r.submitted_icds_dotted}>
                              <strong>Sub:</strong> {r.submitted_icds_dotted || '-'}
                            </Text>
                          </Td>
                          <Td fontSize="xs" fontFamily="mono">
                            <Text color="green.700">
                              <strong>Rev:</strong> {r.revedy_base_cpts || '-'}
                            </Text>
                            <Text color="orange.700">
                              <strong>Sub:</strong> {r.claim_base_cpts || '-'}
                            </Text>
                            {r.cpts_added_by_usn && (
                              <Badge colorScheme="red" variant="subtle" fontSize="10px" mt={1}>
                                + Added: {r.cpts_added_by_usn}
                              </Badge>
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default USNClaimsFirstPassDashboard;
